import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Typography, Collapse } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import Logo from '../../assets/images/logo512.png';
import LogoText from '../../assets/images/logotextlight.png';
import MainLayout from '../../components/MainLayout';
import {
  SIGN_UP_ROUTE,
  PROBLEM_SETS_ROUTE,
  HOMEPAGE_CAPTION,
  HOMEPAGE_FEATURED_TOPICS,
  HomepageFeaturedTopic,
  FAQs,
  FrequentlyAskedQuestion
} from '../../config/constants';
import { contactMessage } from '../../api/accounts';
import LargeButton from '../../components/LargeButton';
import StatusMessages, { StatusMessage } from '../../components/StatusMessages';
import GetStartedIcon from '../../assets/images/getstartedicon.png';
import ProblemSetsIcon from '../../assets/images/problemsetsicon.png';
import styles from './Home.module.css';
import ContactUsForm, { ContactInfo } from '../../components/ContactUsForm';

export interface HomeProps {};

// main website landing page
const Home = (props: HomeProps): JSX.Element | null => {
  const navigate = useNavigate();
  // array of keys of expanded panels
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  // contact us status messages
  const [contactUsStatusMessages, setContactUsStatusMessages] = useState<StatusMessage[]>([]);

  // set document title
  useEffect(() => {
    document.title = 'Sigmoid Academy';
  }, []);

  // handle FAQ expand on change
  const onFAQExpand = ((newKeys: string[]) => {
    setExpandedPanels((oldKeys: string[]) => {
      let keys = newKeys.filter(function(x) {
        return oldKeys.indexOf(x) < 0;
      });

      if (keys.length > 1) {
        keys = [keys[0]];
      }

      return keys;
    })
  });

  // handle contact message submission
  const handleContactUs = async (info: ContactInfo): Promise<void> => {
    // clear status messages
    setContactUsStatusMessages((previousMessages: StatusMessage[]) => []);
    // send contact message
    contactMessage(
      info.email,
      info.subject,
      info.message
    ).then(() => {
      setContactUsStatusMessages([{
        message: 'Your message was submitted. We will be in touch soon.',
        status: 'success'
      }]);
    }
    ).catch((error: any) => {
      if (error.response?.data.detail) {
        setContactUsStatusMessages([{
          message: error.response?.data.detail,
          status: 'error'
        }]);
      } else {
        // collect list of error messages from response
        let errorStatusMessages: StatusMessage[] = [];
        for (let field in error.response?.data as any) {
          let messages = (error.response?.data as any)[field];

          for (let i = 0; i < messages.length; i++) {
            errorStatusMessages.push({
              message: messages[i].charAt(0).toUpperCase() + messages[i].slice(1),
              status: 'error',
            });
          }
        }

        if (errorStatusMessages.length > 0) {
          // set list of errors as status messages
          setContactUsStatusMessages((previousMessages: StatusMessage[]) => [...errorStatusMessages]);
        } else {
          setContactUsStatusMessages([{
            message: 'Unknown error occurred while submitting the message, please try again.',
            status: 'error'
          }]);
        }
      }
    });
  };

  return (
    <div>
      <MainLayout contentWidth={100}>
        <div className={styles.mainContainer}>
          <div className={styles.mainSectionContainer}>
            <div className={styles.leftContainer}>
              <div className={styles.mainLogoContainer}>
                <img
                  alt='Sigmoid Academy Logo'
                  src={Logo}
                  className={styles.mainLogoImg}
                />
                <img
                  alt='Sigmoid Academy Logo Text'
                  src={LogoText}
                  className={styles.mainLogoText}
                />
              </div>
              <div className={styles.mainCaptionContainer}>
                <Typography.Title
                  level={3}
                  className={styles.mainCaption}
                >
                  {HOMEPAGE_CAPTION}
                </Typography.Title>
                <Typography.Title
                  level={4}
                  className={styles.mainCaptionSmall}
                >
                  {HOMEPAGE_CAPTION}
                </Typography.Title>
              </div>
              <div className={styles.buttonsContainer}>
                <Link to={SIGN_UP_ROUTE}>
                  <LargeButton
                    text={<span>Get<br />Started</span>}
                    textLevel={2}
                    textOnRight={true}
                    iconSrc={GetStartedIcon}
                    iconAlt={'Get Started Icon'}
                    iconSize={80}
                    lightBackground={true}
                  />
                </Link>
                <Link to={PROBLEM_SETS_ROUTE}>
                  <LargeButton
                    text={<span>Problem<br />Sets</span>}
                    textLevel={2}
                    textOnRight={false}
                    iconSrc={ProblemSetsIcon}
                    iconAlt={'Problem Sets Icon'}
                    iconSize={80}
                    lightBackground={false}
                  />
                </Link>
              </div>
            </div>
            <div className={styles.rightContainer}>
              <div className={styles.problemSkeletonMainTitleTextContainer}>
                <Typography.Title
                  level={2}
                  className={styles.problemSkeletonMainTitle}
                >
                  Ace your data science interviews
                </Typography.Title>
                <Typography.Text className={styles.problemSkeletonMainText}>
                  Take a hands-on approach to learning data science and machine learning by tackling problems related to a wide range of topics
                </Typography.Text>
              </div>
              <div className={styles.problemSkeletonContainer}>
                <img
                  alt='Problem Skeleton'
                  src='https://raw.githubusercontent.com/alvii147/SigmoidMedia/master/homepage/problem_skeleton/problem_skeleton.gif'
                  className={styles.problemSkeletonImg}
                />
              </div>
            </div>
          </div>
          <div className={styles.featuredTopicsMainContainer}>
            <div className={styles.featuredTopicsMainTitleTextContainer}>
              <Typography.Title
                level={2}
                className={styles.featuredTopicsMainTitle}
              >
                Ace your data science interviews
              </Typography.Title>
              <Typography.Text className={styles.featuredTopicsMainText}>
                Take a hands-on approach to learning data science and machine learning by tackling problems related to a wide range of topics
              </Typography.Text>
            </div>
            <div className={styles.featuredTopicsContainer}>
              {HOMEPAGE_FEATURED_TOPICS.map((fps: HomepageFeaturedTopic) => (
                <div
                  key={fps.title}
                  className={styles.featuredTopic}
                  onClick={() => navigate(PROBLEM_SETS_ROUTE)}
                >
                  <img
                    alt={fps.iconAlt}
                    src={fps.iconSrc}
                    className={styles.featuredTopicIcon}
                  />
                  <Typography.Title
                    level={3}
                    className={styles.featuredTopicTitle}
                  >
                    {fps.title}
                  </Typography.Title>
                  <Typography.Title
                    level={5}
                    className={styles.featuredTopicTitleSmall}
                  >
                    {fps.title}
                  </Typography.Title>
                  <Typography.Text className={styles.featuredTopicText}>
                    {fps.caption}
                  </Typography.Text>
                </div>
              ))}
            </div>
          </div>
          <div className={styles.faqsMainContainer}>
            <Typography.Title
              level={2}
              className={styles.faqsMainTitle}
            >
              Frequently Asked Questions
            </Typography.Title>
            <div className={styles.faqsCollapseContainer}>
              <Collapse
                activeKey={expandedPanels}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                className={styles.faqsCollapse}
                onChange={key => onFAQExpand(Array.isArray(key) ? key : [key])}
              >
                {FAQs.map((FAQ: FrequentlyAskedQuestion, index: number) => (
                  <Collapse.Panel
                    key={index.toString()}
                    header={
                      <Typography.Text
                        className={styles.faqsQuestionText}
                        strong={true}
                      >
                        {FAQ.question}
                      </Typography.Text>
                    }
                    className={styles.faqsCollapsePanel}
                  >
                    <Typography.Text
                      className={styles.faqsAnswerText}
                    >
                      {FAQ.answer}
                    </Typography.Text>
                  </Collapse.Panel>
                ))}
              </Collapse>
            </div>
          </div>
          <div className={styles.contactMessageMainContainer}>
            <Typography.Title
              level={2}
              className={styles.contactMessageMainTitle}
            >
              Contact Us
            </Typography.Title>
            <StatusMessages items={contactUsStatusMessages} />
            <div className={styles.contactMessageFormContainer}>
              <ContactUsForm
                handleContactUs={handleContactUs}
              />
            </div>
          </div>
        </div>
      </MainLayout>
    </div>
  );
};

export default Home;
